

/**
 * Calibration banner for the weekly view: glass panel, blue-tinted artwork and static
 * progress tabs. Text is rendered in the DOM (not baked into the image) so the
 * "STAGE" line can carry the blue accent.
 */
export function CalibrationBadge({ completed = null, total = 4, done = false, className = '', testId = 'calibration-badge' }) {
  const tracked = Number.isFinite(completed);
  return (
    <div
      className={`relative w-full overflow-hidden rounded-2xl border border-white/[0.10] ${className}`}
      style={{
        aspectRatio: '1000 / 324',
        background: 'linear-gradient(135deg, #0C1218 0%, #07090C 55%, #060A12 100%)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.08), 0 14px 34px rgba(0,0,0,0.5)',
      }}
      data-testid={testId}
      data-animating={false}
    >
      {/* Artwork, recoloured to the app's blue */}
      <img
        src="/cal-figure.webp"
        alt=""
        aria-hidden="true"
        draggable={false}
        className="bm-cal-art pointer-events-none absolute inset-y-0 left-0 h-full select-none object-cover object-left"
        style={{ width: '31%', maskImage: 'linear-gradient(90deg, #000 72%, transparent 100%)', WebkitMaskImage: 'linear-gradient(90deg, #000 72%, transparent 100%)' }}
      />
      {/* Glass + depth: dark wash over the art, a cool blue bloom behind the copy */}
      <span className="pointer-events-none absolute inset-0"
            style={{ background: 'linear-gradient(90deg, rgba(4,6,10,0.18) 0%, rgba(4,6,10,0.72) 34%, rgba(4,6,10,0.55) 100%)' }} />
      <span className="pointer-events-none absolute inset-0"
            style={{ background: 'radial-gradient(70% 120% at 42% 55%, rgba(10,132,255,0.16) 0%, transparent 70%)' }} />

      {/* Copy + progress tabs */}
      <div className="absolute inset-0 flex flex-col justify-center pl-[29%] pr-[6%]">
        <p className="font-heading font-black uppercase leading-[0.9] tracking-tight text-white text-[clamp(1rem,5.2vw,2.1rem)]"
           data-testid="calibration-title">
          Calibration
        </p>
        <p className="font-heading font-black uppercase leading-[0.9] tracking-tight text-[clamp(1rem,5.2vw,2.1rem)] bg-clip-text text-transparent"
           style={{ backgroundImage: 'linear-gradient(180deg, #FFFFFF 0%, #E8EDF5 42%, #8A93A3 100%)' }}
           data-testid="calibration-stage-word">
          Stage
        </p>
        <p className="mt-[2.5%] text-white/70 text-[clamp(0.6rem,2.4vw,0.95rem)] leading-tight"
           data-testid="calibration-subtitle">
          Adjusting to your physiology.
        </p>

        <div className="mt-[4%] flex items-center gap-[1.6%]" aria-hidden="true">
          {Array.from({ length: total }, (_, i) => {
            const reached = tracked && i < completed;
            const next = tracked && i === completed;
            return (
              <span
                key={i}
                className={`h-[7px] sm:h-2 flex-1 max-w-[46px] rounded-full ${done ? 'bm-cal-pip-complete' : tracked ? (reached ? 'bm-cal-pip-done' : 'bm-cal-pip-todo') : 'bm-cal-pip'}`}
                data-testid={`calibration-pip-${i + 1}`}
                data-state={done ? 'complete' : tracked ? (reached ? 'done' : next ? 'next' : 'todo') : 'demo'}
              />
            );
          })}
        </div>
      </div>

      <style>{`
        .bm-cal-art { filter: hue-rotate(128deg) saturate(1.35) brightness(1.08) contrast(1.05); }
        .bm-cal-pip, .bm-cal-pip-todo { background: rgba(var(--accent-rgb),0.22); }
        .bm-cal-pip-done { background: var(--accent-hex); box-shadow: 0 0 10px rgba(var(--accent-rgb),0.55); }
        .bm-cal-pip-complete { background: var(--accent-muted); box-shadow: 0 0 10px rgba(var(--accent-muted-rgb),0.5); }
      `}</style>
    </div>
  );
}

export default CalibrationBadge;
